import ApiRequest from './ApiRequest';

/**
 * DelayedApiRequest - 입력 지연을 통한 API 요청 최적화 유틸리티
 * 사용자가 입력하는 동안 API 요청을 지연시키고, 입력이 끝난 후에만 요청을 실행합니다.
 */
class DelayedApiRequest {
    constructor(delay = 500) {
        this.delay = delay; // 기본 지연 시간 500ms
        this.timeoutId = null; // 타이머 ID 저장
        this.pendingRequests = new Map(); // 대기 중인 요청들을 관리
    }

    /**
     * 지연된 GET 요청
     * @param {string} url - 요청 URL
     * @param {string} key - 요청을 구분하는 키 (같은 키의 이전 요청은 취소됨)
     * @param {function} callback - 응답을 처리할 콜백 함수
     * @param {number} customDelay - 커스텀 지연 시간 (선택사항)
     */
    delayedGet(url, key = 'default', callback, customDelay = null) {
        this.cancelPending(key);
        
        const delay = customDelay || this.delay;
        const timeoutId = setTimeout(async () => {
            try {
                const response = await ApiRequest.get(url);
                // 요청이 완료되면 대기 목록에서 제거
                this.pendingRequests.delete(key);
                callback(response, null);
            } catch (error) {
                this.pendingRequests.delete(key);
                callback(null, error);
            }
        }, delay);

        // 새로운 요청을 대기 목록에 추가
        this.pendingRequests.set(key, timeoutId);
    }

    /**
     * 지연된 POST 요청
     * @param {string} url - 요청 URL
     * @param {object} data - 요청 데이터
     * @param {string} key - 요청을 구분하는 키
     * @param {function} callback - 응답을 처리할 콜백 함수
     * @param {number} customDelay - 커스텀 지연 시간 (선택사항)
     */
    delayedPost(url, data, key = 'default', callback, customDelay = null) {
        this.cancelPending(key);
        
        const delay = customDelay || this.delay;
        const timeoutId = setTimeout(async () => {
            try {
                const response = await ApiRequest.post(url, data);
                this.pendingRequests.delete(key);
                callback(response, null);
            } catch (error) {
                this.pendingRequests.delete(key);
                callback(null, error);
            }
        }, delay);

        this.pendingRequests.set(key, timeoutId);
    }

    /**
     * 특정 키의 대기 중인 요청 취소
     * @param {string} key - 취소할 요청의 키
     */
    cancelPending(key) {
        if (this.pendingRequests.has(key)) {
            clearTimeout(this.pendingRequests.get(key));
            this.pendingRequests.delete(key);
        }
    }

    /**
     * 모든 대기 중인 요청 취소
     */
    cancelAll() {
        this.pendingRequests.forEach((timeoutId) => {
            clearTimeout(timeoutId);
        });
        this.pendingRequests.clear();
    }

    /**
     * 지연 시간 변경
     * @param {number} newDelay - 새로운 지연 시간 (ms)
     */
    setDelay(newDelay) {
        this.delay = newDelay;
    }

    /**
     * 현재 대기 중인 요청 개수 반환
     */
    getPendingCount() {
        return this.pendingRequests.size;
    }

    /**
     * 특정 키의 요청이 대기 중인지 확인
     * @param {string} key - 확인할 요청의 키
     */
    isPending(key) {
        return this.pendingRequests.has(key);
    }
}

// React Hook으로 사용할 수 있는 함수
export const useDelayedApiRequest = (delay = 500) => {
    const delayedRequest = new DelayedApiRequest(delay);
    
    return {
        delayedGet: delayedRequest.delayedGet.bind(delayedRequest),
        delayedPost: delayedRequest.delayedPost.bind(delayedRequest),
        cancelPending: delayedRequest.cancelPending.bind(delayedRequest),
        cancelAll: delayedRequest.cancelAll.bind(delayedRequest),
        setDelay: delayedRequest.setDelay.bind(delayedRequest),
        getPendingCount: delayedRequest.getPendingCount.bind(delayedRequest),
        isPending: delayedRequest.isPending.bind(delayedRequest)
    };
};

// 전역 인스턴스 (싱글톤 패턴)
const globalDelayedApiRequest = new DelayedApiRequest();

export default globalDelayedApiRequest;
